import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Clock, Shield, Users } from 'lucide-react';

export const ProjectWizardIntroPage: React.FC = () => {
  const navigate = useNavigate();

  const handleStart = () => {
    navigate('/projects/new/step/1');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img 
              src="/logo_CRESS_Guadeloupe.jpg" 
              alt="CRESS Guadeloupe" 
              className="h-24 w-auto object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Créer votre projet ESS
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Nous allons vous accompagner étape par étape dans la création de votre projet 
            d'Économie Sociale et Solidaire en Guadeloupe.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Process Overview */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                Comment ça fonctionne ?
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Questionnaire guidé</h3>
                    <p className="text-gray-600 text-sm">
                      12 questions pour mieux comprendre votre projet et vos besoins
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Documents requis</h3>
                    <p className="text-gray-600 text-sm">
                      Téléversement de vos documents (KBIS, RIB, description du projet)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Validation et soumission</h3>
                    <p className="text-gray-600 text-sm">
                      Vérification de votre dossier avant soumission finale
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                Vos avantages
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Sauvegarde automatique de votre progression</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Reprise possible à tout moment</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Données sécurisées et confidentielles</span>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">Accompagnement personnalisé CRESS</span>
                </div>
              </div>
            </div>
          </div>

          {/* Time Estimate */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6 text-blue-600" />
              <div>
                <h3 className="font-semibold text-blue-900">Temps estimé</h3>
                <p className="text-blue-700">
                  Environ 15-20 minutes pour le questionnaire + temps de préparation des documents
                </p>
              </div>
            </div>
          </div>

          {/* Required Documents Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-gray-900 mb-4">Documents à préparer</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center">
                <FileText className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <h4 className="font-medium text-gray-900">KBIS</h4>
                <p className="text-sm text-gray-600">ou équivalent</p>
                <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (max 10MB)</p>
              </div>
              <div className="text-center">
                <FileText className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <h4 className="font-medium text-gray-900">RIB</h4>
                <p className="text-sm text-gray-600">Relevé d'identité bancaire</p>
                <p className="text-xs text-gray-500 mt-1">PDF, PNG, JPG (max 5MB)</p>
              </div>
              <div className="text-center">
                <FileText className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <h4 className="font-medium text-gray-900">Description</h4>
                <p className="text-sm text-gray-600">Description détaillée</p>
                <p className="text-xs text-gray-500 mt-1">PDF, DOCX, MD, TXT (max 10MB)</p>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <div className="text-center">
            <button
              onClick={handleStart}
              className="inline-flex items-center space-x-3 px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-semibold text-lg"
            >
              <span>Commencer mon projet</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>
            Besoin d'aide ? Contactez-nous à{' '}
            <a href="mailto:support@cress-guadeloupe.org" className="text-blue-600 hover:text-blue-700">
              support@cress-guadeloupe.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};